// hooks/useDebounce.js
import { useRef, useCallback, useEffect } from "react";

const useDebounce = (functionToDebounce, delay) => {
  const timeoutRef = useRef(null);
  const latestArgsRef = useRef([]);
  const latestFunctionRef = useRef(functionToDebounce);

  useEffect(() => {
    latestFunctionRef.current = functionToDebounce;
  }, [functionToDebounce]);

  const cancel = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const flush = useCallback(() => {
    if (!timeoutRef.current) {
      return;
    }
    clearTimeout(timeoutRef.current);
    timeoutRef.current = null;

    latestFunctionRef.current?.(...latestArgsRef.current);
  }, []);

  const debouncedFunction = useCallback(
    (...args) => {
      latestArgsRef.current = args;
      cancel();
      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = null;
        latestFunctionRef.current?.(...args);
      }, delay);
    },
    [cancel, delay]
  );

  useEffect(() => cancel, [cancel]);

  debouncedFunction.cancel = cancel;
  debouncedFunction.flush = flush;

  return debouncedFunction;
};

export default useDebounce;
